import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import { FeaturedCard } from '@/features/professionals/components/FeaturedCard';
import { SpecializationCard } from '@/features/professionals/components/SpecializationsPanel';
import { HeroBanner } from './HeroBanner';
import { FeatureSpotlightRow } from './FeatureSpotlight';
import { RegisteredHomePage } from './RegisteredHomePage';
import { SocialLinks } from '@/components/SocialLinks';
import {
  useFeaturedProfessionals,
  useSpecializations,
} from '@/features/professionals/hooks/usePublicProfessionals';

export function HomePage() {
  const { isAuthenticated } = useAuth();

  // Authenticated users always land on their own home. PromoHomePage is the
  // anonymous marketing landing. The first-recharge offer inside RegisteredHomePage
  // hides itself once the user has recharged (see hasCompletedFirstRecharge).
  if (isAuthenticated) {
    return <RegisteredHomePage />;
  }

  return <PromoHomePage />;
}

/**
 * Homepage per utenti non autenticati o promo-eligible (rechargeCount <= 1).
 * Mostra prezzi promozionali 0,99€/min su tutte le card.
 */
function PromoHomePage() {
  const [featuredCollapsed, setFeaturedCollapsed] = useState(false);
  const { isAuthenticated } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const registrationOpen = registrationStatus?.registrationEnabled ?? false;
  const navigate = useNavigate();

  // Sticky CTA: track when inline CTA scrolls out of viewport
  const ctaRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [stickyDismissed, setStickyDismissed] = useState(false);

  useEffect(() => {
    if (isAuthenticated) return;
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setShowStickyBar(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAuthenticated]);

  const { data: featured, isLoading: featuredLoading } = useFeaturedProfessionals();
  const { data: specializations } = useSpecializations();

  // Pin every featured card (and the spec card) to the first card's natural
  // height; taller content scrolls inside. Re-measured on resize/layout change.
  const gridRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | undefined>();
  // Pin heights only on multi-column layouts (sm+). On a single mobile column
  // pinning just adds dead vertical space, so let the cards size naturally.
  const [pinHeights, setPinHeights] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const sync = () => setPinHeights(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  useLayoutEffect(() => {
    const first = gridRef.current?.firstElementChild as HTMLElement | undefined;
    if (!first) return;
    const measure = () => setCardHeight(first.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(first);
    return () => ro.disconnect();
  }, [featured, featuredCollapsed]);

  // Selecting a specialization jumps to the full directory, filtered.
  const goToSpecialization = (name?: string) =>
    navigate(name ? `/consulenti?specialization=${encodeURIComponent(name)}` : '/consulenti');

  return (
    <div className="text-brand-navy">
      {/* ── CTA: 7 minuti gratis (auth-gated) ── */}
      {!isAuthenticated && registrationOpen && (
        <section className="px-4 py-6" ref={ctaRef}>
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col gap-3 rounded-xl border border-brand-orange/20 bg-brand-orange/[0.06] px-5 py-4 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
              <div>
                <p className="text-sm font-semibold text-brand-orange">
                  Nuovo su Klaro7?
                </p>
                <p className="text-xs text-neutral-500">
                  Registrati e ottieni 7 minuti di consulenza gratis — tutte le consulenze a 0,99€/min!{' '}
                  <span className="font-semibold uppercase tracking-wide text-brand-orange">Prima ricarica</span>
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-orange-600 min-h-[44px] sm:shrink-0"
              >
                7 Min Gratis
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Hero Banner ── */}
      <HeroBanner />

      {/* ── Featured: Top 7 ── */}
      <section className="px-4 pb-6">
        <div className="mx-auto max-w-6xl">
          <button
            onClick={() => setFeaturedCollapsed(!featuredCollapsed)}
            className="mb-4 flex w-full items-center gap-3"
          >
            <div className="h-px flex-1 bg-gradient-to-r from-brand-orange/30 to-transparent" />
            <h2 className="shrink-0 text-sm font-semibold uppercase tracking-widest text-brand-orange/70">
              Top 7 della Settimana
            </h2>
            <svg
              className={`h-4 w-4 shrink-0 text-brand-orange/50 transition-transform ${featuredCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <div className="h-px flex-1 bg-gradient-to-l from-brand-orange/30 to-transparent" />
          </button>

          {!featuredCollapsed && (
            <>
              {featuredLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" className="border-brand-orange/30 border-t-brand-orange" />
                </div>
              ) : featured && featured.length > 0 ? (
                <div className="space-y-6">
                  {/* Single grid; every card AND the spec card are pinned to the
                      first card's measured height — overflow scrolls inside. */}
                  <div ref={gridRef} className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {featured.map((pro, i) => (
                      <FeaturedCard
                        key={pro.id}
                        professional={pro}
                        rank={i + 1}
                        showPromoPrice
                        height={pinHeights && i !== 0 ? cardHeight : undefined}
                      />
                    ))}
                    {/* "Top 8": specializations as a card right after the last rank */}
                    {specializations && specializations.length > 0 && (
                      <SpecializationCard
                        specializations={specializations}
                        onSelect={goToSpecialization}
                        height={pinHeights ? cardHeight : undefined}
                      />
                    )}
                  </div>
                  {featured.length > 4 && (
                    <div className="relative flex items-center gap-4">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-teal/30 to-brand-teal/30" />
                      <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.18em] text-brand-teal/70">
                        Anche da scoprire
                      </span>
                      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-brand-teal/30 to-brand-teal/30" />
                    </div>
                  )}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-neutral-400">
                  Nessun consulente in evidenza al momento
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── CTA inline: after Top 7 (auth-gated) ── */}
      {!isAuthenticated && registrationOpen && (
        <section className="px-4 pb-6">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-brand-teal/15 bg-gradient-to-r from-brand-teal/[0.05] to-brand-orange/[0.05] px-5 py-5 text-center sm:flex-row sm:justify-between sm:text-left">
              <div>
                <p className="text-sm font-semibold text-brand-navy">
                  Ti piace quello che vedi?
                </p>
                <p className="text-xs text-neutral-500">
                  Prova gratis — 7 minuti di consulenza con il tuo consulente preferito
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-[0_0_12px_rgba(235,114,35,0.2)] transition-all hover:bg-brand-orange/90 hover:shadow-[0_0_18px_rgba(235,114,35,0.3)] min-h-[44px] sm:shrink-0"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                7 Minuti Gratis
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Vedi tutti i consulenti → full directory page ── */}
      <section className="px-4 pb-12">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/consulenti"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-teal/30 bg-brand-teal/5 px-6 py-5 text-base font-bold text-brand-teal transition-all hover:border-brand-teal/50 hover:bg-brand-teal/10 min-h-[44px]"
          >
            Vedi tutti i consulenti
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ── Spotlight: Bonus / Preferiti / Supporto / Lavora con noi ── */}
      <FeatureSpotlightRow />

      {/* ── Trust badges ── */}
      <section className="border-t border-neutral-200 px-4 py-10">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 md:grid-cols-4">
          {[
            { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Disponibili 24/7' },
            { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', label: 'Transazioni sicure' },
            { icon: 'M5 13l4 4L19 7', label: 'Professionisti verificati' },
            { icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', label: 'Chat e telefono' },
          ].map(({ icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10">
                <svg className="h-5 w-5 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                </svg>
              </div>
              <span className="text-xs font-medium text-neutral-500">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-neutral-200 px-4 py-6">
        <div className="mx-auto max-w-6xl text-center">
          <SocialLinks className="mb-3 flex items-center justify-center gap-4" />
          <p className="text-xs text-neutral-400">
            Klaro7 - Consulenza di Cartomanzia Online
          </p>
        </div>
      </footer>

      {/* ── Sticky bottom CTA bar (auth-gated, scroll-triggered) ── */}
      {!isAuthenticated && registrationOpen && showStickyBar && !stickyDismissed && (
        <div className="fixed inset-x-0 bottom-0 z-40 animate-slide-up">
          <div className="border-t border-brand-orange/20 bg-white/95 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur-sm">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
              <p className="text-xs font-medium text-neutral-600 sm:text-sm">
                <span className="font-semibold text-brand-orange">Tutte le consulenze a 0,99€/min</span>
                {' '}— Registrati e inizia subito{' '}
                <span className="font-semibold uppercase tracking-wide text-brand-orange">Prima ricarica</span>
              </p>
              <div className="flex items-center gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center rounded-lg bg-brand-orange px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-orange/90 sm:text-sm min-h-[36px]"
                >
                  Registrati
                </Link>
                <button
                  onClick={() => setStickyDismissed(true)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  aria-label="Chiudi"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
